/**
 * EFormsAdmin.tsx — Admin > Forms & Templates > E-Forms.
 *
 * The corporate dashboard's E-Forms page (v2) for the signed-in account, in
 * the Admin list layout:
 *   - Title, sorted by title (no list order: user, 2026-10-06); search
 *     (title, page title, header and submission messages), Show deleted;
 *   - "Add E-Form" top right: the settings dialog, then the Form Builder for
 *     the new form, as corp;
 *   - a click on a row opens the Form Builder; row actions Manage Fields,
 *     Edit (the settings), Delete / Restore. A deleted form can't be opened in
 *     the builder (the API serves only live forms), so it offers Edit and
 *     Restore.
 *
 * Locked (plan 2026-10-10-001, U7): no Add E-Form, Manage Fields, Edit,
 * Delete or Restore. A row still opens the Form Builder (read-only), and a
 * View icon opens the settings dialog as a viewer; a deleted form's row opens
 * its settings. Nothing says so (R18).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { List, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions, ROW_ACTION_ICON_CLASS, RowActionButton } from '@/components/admin/AdminRowActions';
import { EFormBuilder } from '@/components/admin/EFormBuilder';
import { EFormSettingsModal } from '@/components/admin/EFormSettingsModal';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { getErrorMessage } from '@/services/apiClient';
import { deleteEForm, listEForms, plainText, restoreEForm, type EForm } from '@/services/eformsAdmin.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [{ key: 'title', header: 'Title', sortable: true }, ACTIONS_COLUMN];

function formTitle(f: EForm): string {
  return f.title.trim() || `Form #${f.id}`;
}

const SORT: ListSortCompare<EForm> = {
  title: (a, b) => compareText(formTitle(a), formTitle(b)),
};

export function EFormsAdmin() {
  const [forms, setForms] = useState<EForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<EForm | 'new' | null>(null);
  const [building, setBuilding] = useState<EForm | null>(null);
  const [deleting, setDeleting] = useState<EForm | null>(null);
  const locked = useAdminPageLevel() === 'locked';

  const load = useCallback(async (quiet = false): Promise<EForm[]> => {
    if (!quiet) setLoading(true);
    try {
      const list = await listEForms();
      setForms(list);
      return list;
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load data. Please try again.'));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      forms.filter(
        (f) =>
          (showDeleted || f.active) &&
          (query === '' ||
            [f.title, f.pageTitle, plainText(f.headerMessage), plainText(f.submissionMessage)].some((v) => v.toLowerCase().includes(query))),
      ),
    [forms, showDeleted, query],
  );
  const sorting = useListSort(rows, { initialKey: 'title', compare: SORT });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleSaved(id: number, isNew: boolean) {
    setEditing(null);
    const list = await load(true);
    // As corp: a new form opens in the Form Builder.
    if (isNew) {
      const created = list.find((f) => f.id === id);
      if (created?.active) setBuilding(created);
    }
  }

  async function handleDelete() {
    if (locked || !deleting) return;
    const id = deleting.id;
    try {
      await deleteEForm(id);
      setForms((prev) => prev.map((f) => (f.id === id ? { ...f, active: false } : f)));
      toast.success(`"${formTitle(deleting)}" deleted`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete the e-form'));
    }
  }

  async function handleRestore(form: EForm) {
    if (locked) return;
    try {
      await restoreEForm(form.id);
      setForms((prev) => prev.map((f) => (f.id === form.id ? { ...f, active: true } : f)));
      toast.success('E-form restored');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to restore the e-form'));
    }
  }

  return (
    <AdminListPage
      title="E-Forms"
      subtitle={locked ? 'Electronic forms and their questions' : 'Manage electronic forms with the visual form builder'}
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add E-Form
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search forms..."
          searchLabel="Search e-forms"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="eforms-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading e-forms…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No forms found' : 'No e-forms yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add an e-form to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={COLUMNS} aria-label="E-forms" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((f) => {
              const deleted = !f.active;
              const title = formTitle(f);
              return (
                <tr
                  key={f.id}
                  data-form-id={f.id}
                  className={adminRowClass({ deleted, clickable: true })}
                  onClick={() => (deleted ? setEditing(f) : setBuilding(f))}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="eform-title">
                      {title}
                    </AdminRowName>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowActions>
                      {!deleted && !locked && (
                        <RowActionButton
                          label={`Manage fields of ${title}`}
                          title="Manage Fields"
                          icon={<List className={ROW_ACTION_ICON_CLASS} />}
                          onClick={() => setBuilding(f)}
                        />
                      )}
                      {/* Locked: a live row opens the builder, so its settings open from a View icon. */}
                      <AdminRowAction kind="edit" label={`Edit ${title}`} viewWhenLocked={!deleted} onClick={() => setEditing(f)} />
                      {deleted ? (
                        <AdminRowAction kind="restore" label={`Restore ${title}`} onClick={() => void handleRestore(f)} />
                      ) : (
                        <AdminRowAction kind="delete" label={`Delete ${title}`} onClick={() => setDeleting(f)} />
                      )}
                    </AdminRowActions>
                  </td>
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['form', 'forms']} />
        </>
      )}

      {editing && (
        <EFormSettingsModal
          key={editing === 'new' ? 'new' : editing.id}
          form={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(id, isNew) => void handleSaved(id, isNew)}
        />
      )}

      {building && (
        <EFormBuilder key={building.id} form={building} onClose={() => setBuilding(null)} onSaved={() => void load(true)} />
      )}

      <ConfirmDialog
        isOpen={deleting !== null && !locked}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${formTitle(deleting)}"?` : ''}
        message="It can no longer be sent or filled in. You can restore it from Show deleted."
        confirmLabel="Delete e-form"
        variant="danger"
      />
    </AdminListPage>
  );
}
